'use client';

import { useState } from 'react';
import { deleteServiceCategory } from "@/lib/actions/service-categories";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Trash2, Plus, Image as ImageIcon } from "lucide-react";
import { Link } from "@/i18n/routing";
import { toast } from "sonner";
import { MediaImg } from '@/components/shared/MediaImg';

interface CategoryListProps {
  categories: any[];
}

export function CategoryList({ categories }: CategoryListProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this category? This will not delete the services under it but will remove their association.")) return;
    
    setIsLoading(true);
    try {
      const result = await deleteServiceCategory(id);
      if (result.success) {
        toast.success("Category deleted successfully");
      } else {
        toast.error(result.error || "Failed to delete category");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-navy">Service Categories</h2>
        <Button asChild size="sm">
          <Link href="/admin/services/categories/new">
            <Plus className="h-4 w-4 mr-2" /> New Category
          </Link>
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-light overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Services</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-gray italic font-sans">
                  No categories found.
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>
                    {category.image ? (
                      <MediaImg src={category.image} alt={category.name} className="h-10 w-10 object-cover rounded border border-light" />
                    ) : (
                      <div className="h-10 w-10 bg-light/30 rounded flex items-center justify-center border border-light">
                        <ImageIcon className="h-4 w-4 text-gray" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-bold text-navy">{category.name}</TableCell>
                  <TableCell className="text-sm font-sans text-gray">{category.slug}</TableCell>
                  <TableCell className="font-sans">{category.order}</TableCell>
                  <TableCell className="font-sans">
                    <span className="bg-light px-2 py-0.5 rounded-full text-xs font-bold text-navy">
                      {category.services?.length || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button variant="ghost" size="icon" asChild>
                      <Link href={`/admin/services/categories/${category.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(category.id)} disabled={isLoading}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
